import React, { useEffect, useState, useCallback } from "react";
import Styles from "./PlaceBid.module.css";
import { Modal, Switch, styled } from "@mui/material";
import { Close } from "@mui/icons-material";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import {
  createBid,
  updateBid,
  updateQuantityRequest,
  updateWinnerBidStatus,
} from "../../../../Services/ReverseAuction/AlterApi";
import { useSelector } from "react-redux";
import { getBidHistory } from "../../../../Services/ReverseAuction/GetApi";
import {
  formatDateTimeView,
  formatDateType,
} from "../../../../commons/FormatDate";
import Countdown from "../../../../commons/CountDownTimer/Countdown";
import CircularProgress from "../../../ReverseAuction/Bidding/CircularProgress";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import Fileupload from "../../../../commons/Inputs/FileUpload/Fileupload";

// Styled Switch Component
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "var(--primary-color-text)",
        ...theme.applyStyles("dark", {
          backgroundColor: "var(--primary-color-text)",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(149, 148, 148, 0.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

// Main Component
const PlaceBid = ({
  open,
  handleClose,
  tenderData,
  requisitionData,
  bidStage,
  handleUpdate,
  status,
  viewBids,
  bidBlock,
}) => {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const userData = useSelector((state) => state?.persistedReducer);
  const parentEntityCompanyId = userData?.user?.parentEntityCompanyId;

  // State for Bid Management
  const [bidList, setBidList] = useState(viewBids || []);
  const [bidPrice, setBidPrice] = useState(
    requisitionData?.selfBid?.bidPrice || "",
  );
  const [bidQuantity, setBidQuantity] = useState(
    requisitionData?.selfBid?.bidQuantityMw || "",
  );
  const [bidHistoryList, setBidHistoryList] = useState([]);

  // State for Quantity Request Update
  const [reqEditId, setReqEditId] = useState("");
  const [reqQuantity, setReqQuantity] = useState("");
  const [docId, setDocId] = useState(
    requisitionData?.selfBid?.documentId || null,
  );

  // State for Modal Management and Loading
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    data: "",
    note: "",
  });
  const [isLoading, setIsLoading] = useState(false); // New loading state for submit button

  // --- Utility Functions ---

  const getBidHistoryData = useCallback(async () => {
    if (role === "consumer") {
      setBidHistoryList(bidList);
      return;
    }
    const payload = {
      requisitionId: requisitionData?.id,
      tenderId: tenderData?.data?.id || tenderData?.id,
      bidStage: bidStage,
    };
    const res = await getBidHistory(payload);
    if (res?.data?.code === 200) {
      setBidHistoryList(res?.data?.data || []);
    }
  }, [
    role,
    requisitionData?.id,
    tenderData?.data?.id,
    tenderData?.id,
    bidStage,
    bidList,
  ]);

  // --- Handlers for Input and Actions ---

  const handleReqQuantity = (value, selected) => {
    if (Number(value) >= Number(selected)) {
      return ErrorMessage("Value should be less than Selected Quantity");
    }
    setReqQuantity(value);
  };

  const handleChangeBidRequest = async (actualQty) => {
    if (actualQty === reqQuantity || reqQuantity === 0 || reqQuantity === "") {
      return ErrorMessage("Selected and required quantity is same or empty");
    }

    try {
      setIsLoading(true); // Start loading for this specific action
      const payload = {
        bidIdToQuantityMap: {
          [reqEditId]: Number(reqQuantity),
        },
      };
      const res = await updateQuantityRequest(payload);
      if (res?.data?.code === 200) {
        SuccessMessage(res?.data?.message);
        setReqEditId("");
        setReqQuantity("");
        window?.location?.reload(); // This reloads the whole page, consider a local state update instead
      }
    } catch (error) {
      console.error("Error updating quantity request:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleBidPrice = (value) => {
    if (status?.includes("ra_ongoing")) {
      if (value > requisitionData?.selfBid?.bidPrice) {
        ErrorMessage(
          `Bid Price cannot be greater than ${requisitionData?.selfBid?.bidPrice}`,
        );
      } else {
        setBidPrice(value);
      }
    } else {
      setBidPrice(value);
    }
  };

  const handleBidQuantity = (value) => {
    const prevBidQty = requisitionData?.selfBid?.bidQuantityMw;
    const requiredQty = requisitionData?.requiredQty;

    if (Number(value) > Number(requiredQty)) {
      return ErrorMessage(
        "Bid quantity should not be greater than required quantity",
      );
    }

    if (prevBidQty && Number(value) > Number(prevBidQty)) {
      return ErrorMessage(
        "Bid quantity should not be greater than previous bid quantity",
      );
    }

    setBidQuantity(value);
  };

  const handleBidActivity = async (id, value) => {
    try {
      // You might want a local loading state for each switch if there are many bids
      const payload = {
        bidIdToStatusMap: {
          [id]: value,
        },
      };
      const res = await updateWinnerBidStatus(payload);
      if (res?.data?.code === 200) {
        SuccessMessage(res?.data?.message);
        const updatedBids = bidList?.map((bid) =>
          bid.id === id ? { ...bid, winnerBidStatus: value } : bid,
        );
        setBidList(updatedBids);
      }
    } catch (error) {
      console.error("Error updating bid status:", error);
    }
  };

  const handleSubmit = async () => {
    if (isLoading) return; // Prevent multiple clicks

    const currentLowestBidPrice = requisitionData?.selfBid?.bidPrice;

    if (
      currentLowestBidPrice &&
      Number(currentLowestBidPrice) < Number(bidPrice)
    ) {
      return ErrorMessage(
        "Updated Price should be less than the Current Lowest bid price",
      );
    }

    if (!docId) {
      return ErrorMessage("Please upload the Payment Proof document.");
    }

    try {
      setIsLoading(true);
      let res;

      if (requisitionData?.selfBid) {
        // Update Bid
        const payload = {
          id: requisitionData?.selfBid?.id,
          tenderId: tenderData?.data?.id || tenderData?.id,
          bidPrice: bidPrice,
          bidQuantityMw: bidQuantity,
          requisitionId: requisitionData?.id,
          documentId: docId,
          created_by: userId,
        };
        res = await updateBid(payload);
      } else {
        // Create Bid
        const payload = {
          tenderId: tenderData?.data?.id || tenderData?.id,
          requisitionId: requisitionData?.id,
          initiator: role,
          bidStage: bidStage,
          bidPrice: bidPrice,
          bidQuantityMw: bidQuantity,
          documentId: docId,
          created_by: userId,
        };
        res = await createBid(payload);
      }

      if (res?.data?.code === 200) {
        handleUpdate();
        handleClose();
      }
    } catch (error) {
      console.error("Error submitting bid:", error);
    } finally {
      setIsLoading(false);
      // Close confirmation modal after submission attempt
      setConfirmModal({ open: false, data: "", note: "" });
    }
  };

  // --- Effects ---

  // Fetch bid history on component mount
  useEffect(() => {
    getBidHistoryData();
  }, [getBidHistoryData]);

  // Update bid history list on notification update
  useEffect(() => {
    if (
      notify?.key === "BSRANotifyConsumerBidUpdated" ||
      notify?.key === "BSRANotifyGeneratorBidUpdated"
    ) {
      setBidHistoryList(viewBids || []);
    }
  }, [viewBids, notify]);

  // --- Calculated Values ---

  const tenderId = tenderData?.data?.id || tenderData?.id;
  const isBucketFilled =
    tenderData?.tenderStatus === "BUCKET_FILLED_RA" ||
    tenderData?.tenderStatus === "BUCKET_FILLED";
  const isClosedStatus = status?.includes("closed");

  const selBids =
    isClosedStatus || status?.includes("tender_closing_soon")
      ? bidList?.filter(
          (ele) =>
            ele?.winnerBidStatus === "PENDING" ||
            ele?.winnerBidStatus === "ACCEPTED" ||
            ele?.winnerBidStatus === "AWARDED",
        )
      : bidList;

  const bucketBid = selBids?.reduce(
    (acc, curr) => acc + Number(curr?.selectedQuantity || curr?.bidQuantityMw),
    0,
  );

  // --- Render Helpers (for cleaner JSX) ---

  const renderHeader = () => {
    const headerText =
      role === "consumer"
        ? "View"
        : requisitionData?.selfBid
          ? "Edit"
          : "Place";
    return (
      <div className={`display_flex_row modalHeading ${Styles.headingText}`}>
        <div className={`headingText ${Styles.finalHead}`}>
          {headerText} Bid(s)
        </div>
        <Close
          style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
          onClick={handleClose}
        />
      </div>
    );
  };

  const renderBidDetails = () => (
    <div className={Styles.detailsMainElements}>
      {/* Duration and Countdown */}
      <div className={`display_flex_row`}>
        <div className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}>
          <div>
            {status?.includes("ra_ongoing") ? "Auction" : "Bidding"} Duration
          </div>
          <div>
            {formatDateTimeView(
              status?.includes("ra_ongoing")
                ? tenderData?.auctionStartDateTime
                : tenderData?.bidStartDateTime,
            )}{" "}
            to{" "}
            {formatDateTimeView(
              status?.includes("ra_ongoing")
                ? tenderData?.auctionCloseDateTime
                : tenderData?.bidCloseDateTime,
            )}
          </div>
        </div>
        <Countdown status={status} tenderData={tenderData} type={"bid"} />
      </div>

      {/* Requisition Details and Bid Info */}
      <div className={`display_flex_row ${Styles.reqMainContainer}`}>
        <DetailItem
          title="Start and End Date"
          value={`${formatDateType(
            requisitionData?.dateStartAt,
          )} to ${formatDateType(requisitionData?.dateEndAt)}`}
        />
        <DetailItem
          title="Start and End Time"
          value={`${requisitionData?.slotStartAt} - ${
            requisitionData?.slotEndAt === "23:59"
              ? "24:00"
              : requisitionData?.slotEndAt
          }`}
        />
        <DetailItem
          title="Req Qty (MW)"
          value={`${requisitionData?.requiredQty} (MW)`}
        />
        <DetailItem
          title="Min Qty (MW)"
          value={`${requisitionData?.minimumQty} (MW)`}
        />

        {/* Dynamic Display based on role and status */}
        {isBucketFilled && role === "consumer" ? (
          <div className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}>
            <div className={Styles.bucketFilledMain}>
              <div>Bucket Filled</div>
              <CircularProgress
                value={bidList?.length > 0 ? bucketBid : 0}
                size={40}
                strokeWidth={4}
                required={requisitionData?.requiredQty}
              />
            </div>
          </div>
        ) : (
          <div className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}>
            <div>{role === "consumer" ? "Lowest" : "Self"} Bid</div>
            <div
              className={
                role === "generator"
                  ? Styles.active_text
                  : Styles.active_text_secondary
              }
            >
              {role === "generator"
                ? renderSelfBidInfo()
                : renderLowestBidInfo()}
            </div>
          </div>
        )}

        {role === "generator" && tenderData?.tenderStatus === "ACTIVE_RA" && (
          <div className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}>
            <div>Lowest Bid</div>
            <div className={Styles.active_text_secondary}>
              {renderLowestBidInfo()}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSelfBidInfo = () => {
    if (
      requisitionData?.selfBid?.bidPrice &&
      requisitionData?.selfBid?.bidQuantityMw
    ) {
      return (
        <>
          {requisitionData.selfBid.bidQuantityMw}(MW)/
          {requisitionData.selfBid.bidPrice}(₹/kWh)
        </>
      );
    }
    return <div style={{ textAlign: "center" }}>-</div>;
  };

  const renderLowestBidInfo = () => {
    if (requisitionData?.lowestBidPrice) {
      return <>{requisitionData.lowestBidPrice}(₹/kWh)</>;
    }
    return <div style={{ textAlign: "center" }}>-</div>;
  };

  const DetailItem = ({ title, value }) => (
    <div className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}>
      <div>{title}</div>
      <div>{value}</div>
    </div>
  );

  const handleBankGurentee = (value) => setDocId(value?.id);

  const renderInputFields = () => {
    return (
      <div className={`display_flex_row ${Styles.inputMainCont}`}>
        <div>
          <TextInput
            allowDecimal={true}
            heading={"Bid Quantity (MW)*"}
            value={bidQuantity}
            onChange={handleBidQuantity}
            example="Eg. 20"
            type="number"
          />
        </div>
        <div>
          <TextInput
            allowDecimal={true}
            heading={"Bid Price (₹/kWh)*"}
            value={bidPrice}
            onChange={handleBidPrice}
            example="Eg. 10"
            type="number"
          />
        </div>
        <div>
          <Fileupload
            accept={".pdf"}
            id={"general-terms-conditions"}
            value={docId}
            onChange={handleBankGurentee}
            height={"35"}
            heading={"BG against EMD*"}
            example={"Upload BG (.pdf)"}
            fileType={"pdf"}
            isInPlaceBid={true}
            viewMode={role === "generator" || isClosedStatus}
            previewTitle={"Bank Guarantee Document Preview"}
            documentType={"BANK_GUARANTEE"}
            parentEntityCompanyId={parentEntityCompanyId}
          />
        </div>
      </div>
    );
  };

  const renderBidTable = () => {
    const isBucketTable = isBucketFilled && !isClosedStatus;

    const tableHeading = isBucketTable ? (
      <div
        className={`display_flex_row tableHeading ${Styles.tableHeadingAlter}`}
      >
        <div style={{ width: "5%" }}>Sl.No</div>
        <div style={{ width: "20%" }}>Bid Price (₹/kWh)</div>
        <div style={{ width: "20%" }}>Bid Quantity (MW)</div>
        <div style={{ width: "20%" }}>Selected Quantity (MW)</div>
        <div style={{ width: "10%" }}>Status</div>
        <div style={{ width: "20%" }}>Activity</div>
      </div>
    ) : (
      <div
        className={`display_flex_row tableHeading ${Styles.tableHeadingAlter}`}
      >
        <div style={{ width: "5%" }}>Sl.No</div>
        <div style={{ width: "20%" }}>Bid Quantity (MW)</div>
        <div style={{ width: "20%" }}>Bid Price (₹/kWh)</div>
        {role === "generator" && <div style={{ width: "20%" }}>Bid Time</div>}
      </div>
    );

    const dataList = isBucketTable ? bidList : bidHistoryList;
    const tableData = dataList?.map((ele, index) => (
      <div className={`display_flex_row tableDetails`} key={ele.id || index}>
        <div style={{ width: "5%" }}>{index + 1}</div>
        {isBucketTable ? (
          <>
            <div style={{ width: "20%" }}>{ele?.bidPrice}</div>
            <div style={{ width: "20%" }}>{ele?.bidQuantityMw || "-"}</div>
            {reqEditId === ele?.id ? (
              <div style={{ width: "20%" }} className={Styles.alter_input_cont}>
                <input
                  type="number"
                  value={reqQuantity}
                  onChange={(e) =>
                    handleReqQuantity(e.target.value, ele?.selectedQuantity)
                  }
                />
              </div>
            ) : (
              <div style={{ width: "20%" }}>
                {(ele?.winnerBidStatus === "PENDING"
                  ? ele?.requestedQuantity
                  : ele?.selectedQuantity) || "-"}
              </div>
            )}
            <div
              style={{ width: "10%" }}
              className={`${getStatusClass(ele?.winnerBidStatus)}`}
            >
              {getDisplayStatus(ele?.winnerBidStatus)}
            </div>
            <div
              style={{ width: "20%" }}
              className={`display_flex_row_gap_8 ${Styles.alterContainer}`}
            >
              {renderActivityButtons(ele)}
            </div>
          </>
        ) : (
          <>
            <div style={{ width: "20%" }}>{ele?.bidQuantityMw}</div>
            <div style={{ width: "20%" }}>{ele?.bidPrice}</div>
            {role === "generator" && (
              <div style={{ width: "20%" }}>
                {formatDateTimeView(ele?.createdAt)}
              </div>
            )}
          </>
        )}
      </div>
    ));

    return (
      <>
        {(role === "generator" ||
          (role === "consumer" &&
            status?.includes("tender_closing_soon") &&
            !status?.includes("closed"))) && (
          <div className={Styles.tableContainer}>
            <div className={Styles.bidHistoryContianer}>
              {role === "consumer" ? "Active Bidding" : "Bid History"}
            </div>
            <div className={Styles.detailsMainContainer}>
              {tableHeading}
              <div className={`scroll_bar_y ${Styles.detailsContainer}`}>
                {tableData}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "ACCEPTED":
      case "ALLOTED":
        return "accepted";
      case "PENDING":
        return "pending";
      default:
        return "rejected";
    }
  };

  const getDisplayStatus = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "Accepted";
      case "ABSCOND":
        return "Rejected"; // or Absconded/Canceled? Based on original logic
      case "ALLOTED":
        return "Allotted";
      case "WITHDRAWN":
        return "Withdrawn";
      case "PENDING":
        return "Pending";
      case "REJECTED":
      default:
        return "Rejected";
    }
  };

  const renderActivityButtons = (ele) => {
    const isEditable =
      ["ACCEPTED", "ABSCOND"].includes(ele?.winnerBidStatus) && isBucketFilled;
    const isPending = ele?.winnerBidStatus === "PENDING";

    if (isEditable) {
      if (reqEditId === ele?.id) {
        return (
          <>
            <div
              className={Styles.sendReqButton}
              onClick={() => handleChangeBidRequest(ele?.bidQuantityMw)}
            >
              <DoneIcon
                style={{
                  fontSize: "16px",
                  color: "#f1935c",
                }}
              />
            </div>
            <div
              className={Styles.removeElement}
              onClick={() => {
                setReqEditId("");
                setReqQuantity("");
              }}
            >
              <Close style={{ fontSize: "16px" }} />
            </div>
          </>
        );
      } else {
        return (
          <>
            <div
              className={Styles.removeElement}
              onClick={() => {
                setReqEditId(ele?.id);
                setReqQuantity(ele?.selectedQuantity);
              }}
            >
              <EditIcon
                style={{
                  fontSize: "16px",
                  color: "#6f6d7c",
                }}
              />
            </div>
            <div>
              <AntSwitch
                checked={["ACCEPTED", "PENDING"].includes(ele?.winnerBidStatus)}
                onChange={() =>
                  handleBidActivity(
                    ele?.id,
                    ele?.winnerBidStatus === "ACCEPTED"
                      ? "ABSCOND"
                      : "ACCEPTED",
                  )
                }
              />
            </div>
          </>
        );
      }
    } else if (isPending) {
      return <div className={"pending"}>NA</div>;
    }
    return <>-</>;
  };

  const renderFooterButtons = () => {
    if (isClosedStatus || status?.includes("tender_closing_soon")) {
      return null;
    }

    return (
      <div className={`display_flex_row_gap_8 ${Styles.buttonContianer}`}>
        <div className={"activeButton_type_3"} onClick={handleClose}>
          Cancel
        </div>
        {role === "generator" && (
          <div
            className="activeButton"
            onClick={() =>
              setConfirmModal({
                open: true,
                data: `Do you want to ${
                  requisitionData?.selfBid ? "Update" : "Place"
                } Bid?`,
                note: requisitionData?.selfBid
                  ? `Current ${requisitionData.selfBid.bidQuantityMw}(MW)/${requisitionData.selfBid.bidPrice}(₹/kWh), updated ${bidQuantity}(MW)/${bidPrice}(₹/kWh)`
                  : "EMD Amount will be blocked as per Terms and Condititons.",
              })
            }
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} color="inherit" />
              </div>
            ) : requisitionData?.selfBid ? (
              "Update"
            ) : (
              "Submit"
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {confirmModal?.open && (
        <ConfirmationModal
          open={confirmModal?.open}
          handleClose={() =>
            setConfirmModal({
              open: false,
              data: "",
              note: "",
            })
          }
          data={confirmModal?.data}
          note={confirmModal?.note}
          handleSubmit={handleSubmit}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div
          className={`popupModal ${
            isBucketFilled
              ? Styles.detailsAlterContainer
              : Styles.createEditModalCont
          }`}
        >
          {renderHeader()}
          <div className={Styles.containerMainElem}>
            {renderBidDetails()}
            {role === "generator" && bidBlock && renderInputFields()}
          </div>

          {/* {(bidHistoryList?.length > 0 || bidList?.length > 0) &&
            renderBidTable()} */}
          {renderFooterButtons()}
        </div>
      </Modal>
    </>
  );
};

export default PlaceBid;
